// usePermissions — the caller's resolved permission map for the active
// tenant, via the get_my_permissions() RPC (mig 20260706150000).
//
// UI gating only — RLS + has_perm() are the security boundary. But the UI
// gate is REQUIRED, not decorative: an RLS-denied UPDATE/DELETE silently
// returns 0 rows, so a button the map says the user can't press must not
// render (security playbook, "UI gate is necessary but not the boundary").
//
// Offline: same snapshot pattern as useCurrentUser's LAST_ME — the last
// successful map for (user, tenant) is kept in localStorage so a
// custom-role holder in the field keeps their module set when the RPC
// can't be reached. Fail-closed otherwise: no map → can() is false →
// callers fall back to the enum-role path (the holder's fallback_role),
// never to more access.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { decodeStoredClaims, LAST_PERMS_KEY } from '@/lib/offlineSession'
import { canFrom, type MyPermissions, type PermAction, type AdminAction } from '@/lib/permissions'

// userId is part of the identity this snapshot is FOR, not decoration. Keyed on
// tenant alone, a shared packhouse/farm tablet served the previous user's map to
// the next one: manager A signs out, picker B signs in to the same tenant, the
// RPC blips (offline, weak link — the exact case the snapshot exists for) and B
// gets A's `can('labour','delete')`. The two sibling snapshots (useCurrentUser's
// LAST_ME, useActiveTenant's) both carry this guard already; the header comment
// here claimed the same pattern without it.
interface PermsSnapshot { tenantId: string; userId: string; perms: MyPermissions }

function readLastPerms(tenantId: string | null, userId: string | null): MyPermissions | null {
  if (!tenantId || !userId) return null
  try {
    const raw = localStorage.getItem(LAST_PERMS_KEY)
    if (!raw) return null
    const snap = JSON.parse(raw) as Partial<PermsSnapshot>
    // Fail closed on legacy snapshots written before userId existed: no way to
    // tell whose they are, and "more access than earned" is the wrong default.
    if (!snap.userId || !snap.perms) return null
    return snap.tenantId === tenantId && snap.userId === userId ? snap.perms : null
  } catch { return null }
}

function writeLastPerms(tenantId: string, userId: string, perms: MyPermissions): void {
  try {
    localStorage.setItem(LAST_PERMS_KEY, JSON.stringify({ tenantId, userId, perms } satisfies PermsSnapshot))
  } catch { /* quota/unavailable — best effort */ }
}

export function usePermissions() {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  // JWT subject — who the stored map belongs to. Read per call rather than
  // held in state so a sign-in on a shared device is picked up immediately.
  const userId = decodeStoredClaims()?.sub ?? null

  const query = useQuery<MyPermissions | null>({
    // Keyed per (tenant, user) so neither a tenant switch nor a user swap on a
    // shared device can serve a stale map.
    queryKey: ['my-permissions', tenantId, userId],
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_permissions')
      if (error) {
        // Offline / RPC unreachable — reuse the last-known map for THIS
        // (user, tenant) pair (see useCurrentUser's symmetric LAST_ME
        // handling); a missing or foreign snapshot resolves to null → fail
        // closed.
        return readLastPerms(tenantId, userId)
      }
      const perms = (data ?? null) as MyPermissions | null
      if (perms && tenantId && userId) writeLastPerms(tenantId, userId, perms)
      return perms
    },
  })

  const perms = query.data ?? null
  return {
    ...query,
    perms,
    // True while the map is the authority (a live custom tenant role).
    isCustomRole: perms?.custom ?? false,
    can: (module: string, action: PermAction | AdminAction) => canFrom(perms, module, action),
  }
}
